import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDDSGetData } from "../../dds/useDDSGetData";
import { dds, getSearchList } from "../../dds/ddsClient";
import { withAuthContext } from "../../dds/withAuthContext";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import apiService from "../../services/api.service";
import { getStoredSession } from "../../utils/sessionManager";
import { deriveRendererLock } from "../../utils/rendererLock";
import sessionService from "../../services/session.service";
import { Loader } from "../../components/atoms";
import ManchsterLogo from "../../assets/images/ManchesterLogo.png";
import GrimsbyTownFCLogo from "../../assets/images/GrimsbyTownFCLogo.png";
import FixtureSelectionList from "../Fixtureselection/FixtureSelectionList";
import TXLive from "./TXLive";
import { parseFixtureTs, isUpcomingOrLive, formatLocalTime } from "../../utils/fixtureTime";
import { resolveTeamLogo } from "../../utils/teamBadge";
import { buildOrgPath } from "../../utils/routePaths";

const getRendererId = (renderer) => renderer?.renderer_id || renderer?.id || "";

// Logo resolution now lives in utils/teamBadge so this page and TXLive cannot
// drift apart — they previously held two copies with different fallbacks. It
// prefers the scene's own badge folder and falls back to the env bucket; see
// that file for why a badge is a scene asset rather than a build-time constant.

// Pick the "current" week — the round of the nearest upcoming fixture
// (>= today). If every fixture is in the past, fall back to the latest
// fixture's week. Matches Aether's getCurrentWeek (BB_AdminPanelUI/
// src/pages/Fixture.tsx) so operators see the same default.
const getCurrentWeek = (fixtures) => {
  if (!fixtures || fixtures.length === 0) return "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sorted = [...fixtures]
    .filter((f) => f.date)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const upcoming = sorted.find((f) => new Date(f.date) >= today);
  const pick = upcoming || sorted[sorted.length - 1];
  return pick?.week != null ? String(pick.week) : "";
};

// Inline preset — picks up fixture_week and team_logo_url alongside the
// fields the library's fixtureListPreset already had. We don't extend the
// library here to avoid the dds-client publish cycle; same pattern Aether
// uses in services/fixtures.services.js.
const buildFixtureListPreset = ({ competition_id, season_id, page = 1, limit = 3000 }) => ({
  keys: [
    "fixture.fixture_id",
    "fixture.fixture_date",
    "fixture.fixture_time",
    "fixture.fixture_week",
    "fixture.home_team.team_name",
    "fixture.home_team.team_short_name",
    "fixture.home_team.team_tricode",
    "fixture.home_team.team_logo_url",
    "fixture.away_team.team_name",
    "fixture.away_team.team_short_name",
    "fixture.away_team.team_tricode",
    "fixture.away_team.team_logo_url",
    "fixture.venue.venue_name",
  ],
  context: { competition_id, season_id },
  pagination: { page, limit },
});

// Competition list. A PRESET rather than a dds.searchData call: the
// per-organization allowlist is applied to the getStaticData competition branch
// (BB_DataDistributionService resolver.service.js). OPUS-92 org-scoped
// searchData as well, but only for the ENTITY collections
// (team/player/staff/official — see DDS modules/org/orgScope.js); `competition`
// is not among them, so a searchData here would still return every competition
// in the database to every org. Mirrors Aether's COMPETITIONS_PRESET
// (BB_AdminPanelUI/src/services/competition.service.js).
//
// profile_id comes back with each row so selecting a competition can record the
// profile it belongs to, which is what scopes the rest of the app.
const COMPETITIONS_PRESET = {
  keys: [
    "competition.competition_id",
    "competition.competition_name",
    "competition.profile_id",
  ],
  pagination: { page: 1, limit: 200 },
};

// LAST-RESORT competition, used only when the org allowlist resolves to nothing
// at all — see the reconcile effect below. It is deliberately NOT the initial
// state: seeding it there is what used to send an operator whose org has no
// Premier League access straight to a competition they cannot see. Reached only
// once the list has loaded and come back empty, where the alternative is a page
// with no competition and no way to open one.
const DEFAULT_COMPETITION_ID = "2kwbbcootiqqgmrzs6o5inle5";
const DEFAULT_SEASON_ID = "51r6ph2woavlbbpk8f29nynf8";

// getStaticData returns the collection under its own name — getSearchList
// (searchData's { list, meta } shape) now lives in ddsClient.js, shared with
// CISPage's useCisMatchData.js — see that file's import above.
const getPresetList = (res, collection) =>
  Array.isArray(res?.[collection]) ? res[collection] : [];

/**
 * Competition/season hints from the launch URL (Aether appends them alongside
 * the fixture). Purely an optimisation: they let the pickers paint the right
 * values immediately instead of waiting for the fixture lookup below. The
 * lookup still runs and still wins, so a stale or wrong hint cannot survive.
 *
 * Read once, at mount. The navigate effect rewrites the URL to the canonical
 * /competition/:orgId/fixture/:id/renderer/:id path and drops the query string,
 * so there is no second chance to read it.
 */
const readLaunchContext = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      competitionId: p.get("competition_id") || "",
      seasonId: p.get("season_id") || "",
    };
  } catch {
    return { competitionId: "", seasonId: "" };
  }
};

export default function TXLivePage({
  fixtureOverlayOpen,
  rendererOverlayOpen,
  // OPUS-1505 — pure pass-through to TXLive, which is the only component where
  // the DDS snapshot (fixtureState) is in scope. The gate is applied upstream in
  // AppRoutes; this shell just carries the flag.
  ddsViewerOpen,
  onCloseDdsViewer,
  isLive,
  isNleEnabled,
  onCloseFixtureOverlay,
  onCloseRendererOverlay,
  onOpenFixtureOverlay,
  onOpenRendererOverlay,
  onSelectionDisplayChange,
  urlFixtureId,
  urlRendererId,
  rendererLockInfo,
  rendererDeleted,
  onLineupErrorsChange,
  onBrokerStatusChange,
}) {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const session = getStoredSession();

  const [selectedFixtureId, setSelectedFixtureId] = useState(
    () => urlFixtureId || localStorage.getItem("selectedFixtureId") || ""
  );
  const [selectedRendererId, setSelectedRendererId] = useState(
    () => urlRendererId || (() => {
      const stored = JSON.parse(localStorage.getItem("rendererSelection") || "{}");
      return stored?.master || "";
    })()
  );
  const [rendererWasChanged, setRendererWasChanged] = useState(false);
  // Scene filter on the renderer-change overlay. "" = no filter; otherwise
  // the literal scene name (option.scene?.name) is matched against the
  // renderer list. Reset whenever the overlay opens so a stale filter
  // doesn't hide every option after a refresh.
  const [rendererSceneFilter, setRendererSceneFilter] = useState("");
  useEffect(() => {
    if (rendererOverlayOpen) setRendererSceneFilter("");
  }, [rendererOverlayOpen]);
  useEffect(() => {
    if (urlFixtureId !== undefined) setSelectedFixtureId(urlFixtureId || "");
    if (urlRendererId !== undefined) setSelectedRendererId(urlRendererId || "");
  }, [urlFixtureId, urlRendererId]);

  const [date, setDate] = useState(null);
  // Week filter defaults to "current week" (round of the nearest upcoming
  // fixture) on first fixture-data load — same default Aether uses.
  // "" while uninitialized, "all" means "no week filter", otherwise a
  // stringified fixture_week value.
  const [selectedWeek, setSelectedWeek] = useState("");
  const [hasInitializedWeek, setHasInitializedWeek] = useState(false);
  // 'week' = current Week/Date/search filter behaviour. 'upcoming' = mirror
  // aether's Upcoming tab: future + in-flight fixtures, sorted chronologically,
  // ignoring the week dropdown.
  const [viewMode, setViewMode] = useState("week");
  const [search, setSearch] = useState("");

  useEffect(() => {
    // Only two routes exist per namespace: the bare org path and the full
    // fixture+renderer path — there's no fixture-only URL. So when a fixture
    // IS selected but the renderer momentarily isn't (e.g. the renderer picker
    // was just force-opened after its renderer was found deleted — see the
    // clearing effect below), do NOT collapse to the bare org URL: that drops
    // the fixture from the route params entirely, which on remount re-reads
    // urlFixtureId as undefined and can lose the fixture selection too. Simply
    // skip the navigate and keep the current URL until a new renderer is
    // chosen (or the fixture itself is cleared) — the picker overlay, not the
    // URL, is what surfaces the "pick a renderer" state in the meantime.
    if (selectedFixtureId && !selectedRendererId) return;

    const targetPath = buildOrgPath(orgId, {
      fixtureId: selectedFixtureId,
      rendererId: selectedRendererId,
    });
    if (pathname !== targetPath) {
      navigate(targetPath, { replace: true });
    }
  }, [selectedFixtureId, selectedRendererId, orgId, navigate, pathname]);

  // Operator-selectable competition, persisted across reloads; its season is
  // resolved automatically. Starts EMPTY on purpose — an empty value means "not
  // chosen yet" and is resolved from the org's allowed list once it loads.
  // Seeding a Premier League id here is what used to send every operator whose
  // org has no PL access to a competition they cannot see.
  // DEFAULT_COMPETITION_ID still exists, but only as the last resort in the
  // reconcile effect below, for when that list comes back empty.
  const [launchContext] = useState(readLaunchContext);
  const [competitionId, setCompetitionId] = useState(
    () => launchContext.competitionId || localStorage.getItem("driveCompetitionId") || ""
  );
  const [seasonId, setSeasonId] = useState(
    () => launchContext.seasonId || localStorage.getItem("driveSeasonId") || ""
  );
  const [competitions, setCompetitions] = useState([]);
  const [competitionsLoading, setCompetitionsLoading] = useState(true);
  const [seasons, setSeasons] = useState([]);
  // Which fixture's context has been resolved from the fixture document itself.
  // null while a resolution is outstanding, which gates the reconcile effect
  // below so it can't adopt competitions[0] over a fixture we're mid-resolving.
  const [resolvedContextFor, setResolvedContextFor] = useState(
    () => (selectedFixtureId ? null : "")
  );

  // Fixtures THIS component chose (auto-select, safety net, the operator's row
  // click). Each of those writes selectedFixtureId, which the URL-sync effect
  // above then pushes into the route — so urlFixtureId changes without the
  // operator having followed a link. Resolution below must ignore those: the
  // component already knows which competition it is looking at, and resolving
  // them let a self-made selection dictate competitionId, which reverted the
  // operator's competition choice (OPUS-1487 regression).
  const selfSelectedFixtureRef = useRef(null);
  const selectFixture = useCallback((fixtureId) => {
    selfSelectedFixtureRef.current = fixtureId || null;
    setSelectedFixtureId(fixtureId);
  }, []);

  // OPUS-1487: a deep link only carries fixtureId/rendererId, not the
  // fixture's competition/season — and competitionId/seasonId above default
  // to whatever the picker last used (or Premier League). Until we've
  // confirmed the deep-linked fixture actually belongs to that competition,
  // the fixtures list below is fetched for the wrong competition, so the
  // "safety net" effect (fixture missing from the loaded list) would stomp
  // the URL's fixture with fixtures[0] of whatever's currently loaded. This
  // flag blocks that effect until resolution finishes (or there's nothing to
  // resolve).
  const [resolvingUrlFixture, setResolvingUrlFixture] = useState(
    () => Boolean(urlFixtureId)
  );
  // This effect no longer fetches. The resolve-context effect below issues the
  // identical lookup — same collection, same filters, same fields, same limit —
  // and additionally checks the fixture's competition against the org's allowed
  // list before adopting it. Running both meant every deep link resolved the
  // same fixture twice, and this one wrote driveCompetitionId/driveSeasonId
  // WITHOUT that entitlement check, so a link into a competition the org cannot
  // see was briefly adopted before the other effect revoked it.
  //
  // What remains is only the gate, now derived from that effect's progress.
  useEffect(() => {
    // Only gate for a fixture that ARRIVED from the URL — never one this
    // component just selected and pushed back into the route.
    if (!urlFixtureId || urlFixtureId === selfSelectedFixtureRef.current) {
      setResolvingUrlFixture(false);
      return;
    }
    // Both conditions matter. `selectedFixtureId === urlFixtureId` releases the
    // gate when the resolve REJECTED this fixture (unknown, or a competition
    // this org may not see): that path clears selectedFixtureId, and the
    // re-run then resets resolvedContextFor to "", which on its own would hold
    // the gate — and isLoading with it — up forever.
    setResolvingUrlFixture(
      selectedFixtureId === urlFixtureId && resolvedContextFor !== urlFixtureId
    );
  }, [urlFixtureId, selectedFixtureId, resolvedContextFor]);

  // Load the competitions this organization is entitled to (drive selector).
  // Server-side filtered — see COMPETITIONS_PRESET.
  useEffect(() => {
    let active = true;
    setCompetitionsLoading(true);
    dds
      .getStaticData(withAuthContext(COMPETITIONS_PRESET))
      .then((res) => { if (active) setCompetitions(getPresetList(res, "competition")); })
      .catch(() => { if (active) setCompetitions([]); })
      .finally(() => { if (active) setCompetitionsLoading(false); });
    return () => { active = false; };
  }, []);

  // Resolve a fixture's REAL competition/season from the fixture document, and
  // the profile that follows from them.
  //
  // A fixture id reaches this page from three places — Aether's launch URL, the
  // Dashboard's session-built deep link, and a stored selection — and in none of
  // them is the fixture guaranteed to belong to the competition currently
  // selected. Previously the context was only recorded when the fixture happened
  // to appear in the loaded fixture list (see the persistence effect below), but
  // that list is scoped by the very competition we are trying to determine. A
  // fixture from anywhere else therefore kept the PREVIOUS competition/season,
  // and TXLive fetched with a mismatched (or empty) context — the fixture in the
  // URL simply never loaded.
  //
  // One lookup, and the fixture row already carries competition_id/season_id.
  // The full fixture fetch cannot do this itself: asking it with only a
  // fixture_id returns the fixture but leaves competition and season empty
  // (DDS scopes those roots by competition_id), and profile_id lives on the
  // competition — so the profile would silently resolve to null.
  useEffect(() => {
    if (!selectedFixtureId) {
      setResolvedContextFor("");
      return;
    }
    if (resolvedContextFor === selectedFixtureId) return;
    // The resolved competition is checked against the org's allowed list, so
    // wait for that list before deciding anything.
    if (competitionsLoading) return;

    let cancelled = false;
    dds
      .searchData({
        collection: "fixture",
        filters: { fixture_id: selectedFixtureId },
        fields: ["fixture_id", "competition_id", "season_id"],
        pagination: { page: 1, limit: 1 },
      })
      .then((res) => {
        if (cancelled) return;
        const row = getSearchList(res)[0];
        const compId = row?.competition_id;
        const seaId = row?.season_id;
        const allowedComp = compId
          ? competitions.find((c) => c.competition_id === compId)
          : null;

        // Unknown fixture, or one whose competition this org is not entitled to.
        // Deliberately NOT falling back to a default competition: quietly
        // loading the wrong match minutes before going on air is the failure
        // that actually costs something. Drop the selection and let the picker
        // (opened below) be the way forward. This is also what stops a deep
        // link being a way around the org filtering on the competition list.
        if (!row || !allowedComp) {
          setResolvedContextFor(selectedFixtureId);
          setSelectedFixtureId("");
          localStorage.removeItem("selectedFixtureId");
          localStorage.removeItem("selectedFixture");
          localStorage.removeItem("selectedCompetitionId");
          localStorage.removeItem("selectedSeasonId");
          onOpenFixtureOverlay?.();
          return;
        }

        setCompetitionId(compId);
        setSeasonId(seaId);
        // Both pairs, deliberately. driveCompetitionId/driveSeasonId back the
        // pickers; selectedCompetitionId/selectedSeasonId are what TXLive reads
        // to fetch match data. Writing only one leaves the two disagreeing.
        localStorage.setItem("driveCompetitionId", compId);
        localStorage.setItem("driveSeasonId", seaId);
        localStorage.setItem("selectedCompetitionId", compId);
        localStorage.setItem("selectedSeasonId", seaId);
        setResolvedContextFor(selectedFixtureId);
      })
      .catch(() => {
        // Network/DDS failure — leave the selection alone rather than clearing
        // it on a transient error, but stop gating the reconcile effect.
        if (!cancelled) setResolvedContextFor(selectedFixtureId);
      });

    return () => { cancelled = true; };
  }, [selectedFixtureId, resolvedContextFor, competitions, competitionsLoading, onOpenFixtureOverlay]);

  // Reconcile the stored competition against what this org may actually see.
  // Three cases, all resolved here so the rest of the page can assume
  // competitionId is always something the operator is entitled to:
  //   - nothing stored            -> adopt the first allowed competition
  //   - stored but not allowed    -> discard it and adopt the first allowed
  //   - stored and allowed        -> keep it
  // The middle case matters: a competition selected before org filtering
  // existed (or since revoked) is still sitting in localStorage, and silently
  // honouring it would let the picker show data the org is no longer entitled
  // to. Clearing the fixture too, since it belonged to the old competition.
  useEffect(() => {
    if (competitionsLoading) return;
    // A fixture whose context is still being resolved owns the competition —
    // adopting competitions[0] here would clobber the deep link a moment before
    // the resolver sets the right one.
    if (resolvedContextFor === null) return;

    // The allowlist resolved to NOTHING — an org with no competitions mapped, or
    // a DDS/UMS lookup that failed. Without this the operator is left on a page
    // with no competition selected and no entry in the picker to select, which
    // is a dead end rather than a degraded state. Fall back to the default so
    // there is always something to open. Only when nothing is chosen already:
    // a stored or deep-linked competition is still the operator's, and a failed
    // lookup is no reason to drag them off it.
    if (competitions.length === 0) {
      if (!competitionId) {
        setCompetitionId(DEFAULT_COMPETITION_ID);
        setSeasonId(DEFAULT_SEASON_ID);
        localStorage.setItem("driveCompetitionId", DEFAULT_COMPETITION_ID);
        localStorage.setItem("driveSeasonId", DEFAULT_SEASON_ID);
      }
      return;
    }

    if (competitions.some((c) => c.competition_id === competitionId)) return;

    const next = competitions[0];
    setCompetitionId(next.competition_id);
    localStorage.setItem("driveCompetitionId", next.competition_id);
    // The stored season/fixture belong to the competition we just discarded.
    localStorage.removeItem("driveSeasonId");
    setSeasonId("");
    setSelectedFixtureId("");
    setHasInitializedWeek(false);
  }, [competitions, competitionsLoading, competitionId, resolvedContextFor]);

  // The selected competition's profile, DERIVED rather than written.
  //
  // This replaces four separate setActiveProfileId calls that each had to
  // remember to fire alongside a competition change — and two paths that
  // changed the competition without one, so the profile could be left pointing
  // at the previous competition. Deriving it from the same two values the
  // competition itself comes from makes that class of drift impossible: if
  // competitionId changes, this changes with it.
  //
  // Blank until the competitions list loads, or when the selected competition
  // is not in it (an org without entitlement). resolveProfileId falls through
  // to the fixture's own profile and then the default, so blank degrades
  // rather than asserting the wrong provider.
  const selectedProfileId = useMemo(
    () => competitions.find((c) => c.competition_id === competitionId)?.profile_id || "",
    [competitions, competitionId],
  );

  // Load the chosen competition's seasons whenever it changes.
  useEffect(() => {
    if (!competitionId) { setSeasons([]); return; }
    let active = true;
    dds
      .searchData({
        collection: "season",
        filters: { competition_id: competitionId },
        fields: ["season_id", "season_name", "competition_id"],
        pagination: { page: 1, limit: 200 },
      })
      .then((res) => { if (active) setSeasons(getSearchList(res)); })
      .catch(() => { if (active) setSeasons([]); });
    return () => { active = false; };
  }, [competitionId]);

  // `seasons` still holds the PREVIOUS competition's rows until the fetch above
  // lands, and the auto-pick below acts the moment seasonId stops matching —
  // which is immediately after a competition switch. Picking from those rows
  // wrote the old competition's season straight back over the operator's
  // choice. The rows carry competition_id (requested above), so scope them
  // during render instead of trusting arrival order.
  const competitionSeasons = useMemo(
    () => seasons.filter((s) => s.competition_id === competitionId),
    [seasons, competitionId],
  );

  // Auto-pick (and persist) a season for the chosen competition; keep the
  // current one if it belongs to this competition, else use the first.
  useEffect(() => {
    if (competitionSeasons.length === 0) return;
    if (competitionSeasons.some((s) => s.season_id === seasonId)) return;
    const nextSeasonId = competitionSeasons[0].season_id;
    setSeasonId(nextSeasonId);
    localStorage.setItem("driveSeasonId", nextSeasonId);
  }, [competitionSeasons, seasonId]);

  const handleCompetitionChange = (newCompetitionId) => {
    if (newCompetitionId === competitionId) return;
    setCompetitionId(newCompetitionId);
    localStorage.setItem("driveCompetitionId", newCompetitionId);
    // No profile write here: selectedProfileId derives from competitionId, so
    // setting the competition above is what changes the profile.
    localStorage.removeItem("driveSeasonId"); // auto-season repicks + persists
    setSeasonId("");
    selectFixture("");
    setHasInitializedWeek(false);
  };

  // Operator picks a season within the chosen competition. Switching season
  // re-scopes the fixture list, so drop any selected fixture (it belongs to the
  // old season) and re-init the week filter, mirroring the competition handler.
  const handleSeasonChange = (newSeasonId) => {
    if (newSeasonId === seasonId) return;
    setSeasonId(newSeasonId);
    localStorage.setItem("driveSeasonId", newSeasonId);
    selectFixture("");
    setHasInitializedWeek(false);
  };

  const { data: fixtureData, loading: fixturesLoading } = useDDSGetData(
    buildFixtureListPreset({
      competition_id: competitionId,
      season_id: seasonId,
      page: 1,
      limit: 3000,
    }),
    { enabled: Boolean(competitionId && seasonId) }
  );

  const [renderers, setRenderers] = useState([]);
  const [renderersLoading, setRenderersLoading] = useState(true);

  // Extracted so it can also be called on-demand when a renderer is found
  // deleted (below) — the org-level list is otherwise only fetched once per
  // mount and can go stale: a renderer deleted after this list was fetched
  // (server-side cached up to 5 min, per Renderer Service) would otherwise
  // keep showing as a selectable option in the picker even though picking it
  // just bounces the operator right back into the deleted-renderer flow.
  const fetchRenderers = useCallback(async () => {
    if (!session?.orgId) {
      setRenderersLoading(false);
      return;
    }
    try {
      setRenderersLoading(true);
      const res = await apiService.getRenderersByOrganization(session.orgId);
      setRenderers(res.renderers || []);
    } catch {
      setRenderers([]);
    } finally {
      setRenderersLoading(false);
    }
  }, [session?.orgId]);

  useEffect(() => {
    fetchRenderers();
  }, [fetchRenderers]);

  // The renderer whose scene supplies the badge folder. On the fixture-selection
  // screen a renderer is often not chosen yet, in which case resolveTeamLogo
  // falls back to the env bucket — the previous behaviour.
  const activeRenderer = useMemo(
    () => renderers.find((r) => getRendererId(r) === selectedRendererId) || null,
    [renderers, selectedRendererId],
  );

  const fixtures = useMemo(() => {
    if (!fixtureData || !Array.isArray(fixtureData.fixture)) return [];
    return fixtureData.fixture
      .map((fx) => {
        const home = fx.home_team?.[0];
        const away = fx.away_team?.[0];
        const venue = fx.venue?.[0];
        return {
          id: fx.fixture_id,
          competition_id: fx.competition_id,
          season_id: fx.season_id,
          team1: home?.team_name ?? "—",
          team2: away?.team_name ?? "—",
          team1Tricode: home?.team_tricode ?? null,
          team2Tricode: away?.team_tricode ?? null,
          // Per-team logo resolution (was hardcoded to ManchsterLogo /
          // GrimsbyTownFCLogo for ALL fixtures — every row showed the
          // same two crests). Fallback to the legacy assets only when
          // both team_logo_url and team_tricode are unavailable.
          team1Logo: resolveTeamLogo(home, { renderer: activeRenderer, fallback: ManchsterLogo }),
          team2Logo: resolveTeamLogo(away, { renderer: activeRenderer, fallback: GrimsbyTownFCLogo }),
          week: fx.fixture_week ?? null,
          date: fx.fixture_date ? fx.fixture_date.replace("Z", "") : null,
          kickoff: fx.kick_off_time ? fx.kick_off_time.slice(0, 5) : "--:--",
          // Local-zone equivalent for the row's kick-off time, plus the
          // raw timestamp + status so the "Upcoming" filter can pick
          // matches without re-parsing strings.
          kickoffTs: parseFixtureTs(fx.fixture_date, fx.kick_off_time),
          status: fx.fixture_status ?? null,
          local: formatLocalTime(parseFixtureTs(fx.fixture_date, fx.kick_off_time)),
          location: venue?.venue_name ?? "TBD",
        };
      })
      .sort((a, b) => {
        if (!a.date || !b.date) return 0;
        return new Date(a.date) - new Date(b.date);
      });
  }, [fixtureData, activeRenderer]);

  // Auto-select first fixture when none is selected. Skipped while a deep
  // link's competition/season is still resolving (see resolvingUrlFixture
  // above) — the currently-loaded fixtures belong to the wrong competition
  // at that point, so fixtures[0] would be the wrong match.
  useEffect(() => {
    if (resolvingUrlFixture) return;
    if (!selectedFixtureId && fixtures.length > 0) {
      selectFixture(fixtures[0].id);
    }
  }, [fixtures, selectedFixtureId, resolvingUrlFixture, selectFixture]);

  // Safety net: if retained fixture no longer exists, recover immediately.
  // Also skipped while resolvingUrlFixture — otherwise this fires on the
  // stale (wrong-competition) fixtures list before the resolved
  // competitionId/seasonId above have re-fetched it, and stomps the
  // deep-linked fixture with fixtures[0] of the wrong competition.
  //
  // Also skipped until the selected fixture's own competition/season have been
  // resolved (resolvedContextFor, above). A stored fixture — e.g. a short link
  // whose session has no renderer, so the fixture isn't in the URL — isn't
  // covered by resolvingUrlFixture, and in a browser with no stored
  // competition the list is disabled: empty with loading false. This took
  // that as "fixture gone" and cleared it, and auto-select then opened the
  // season's first fixture instead.
  useEffect(() => {
    if (resolvingUrlFixture || fixturesLoading || !selectedFixtureId) return;
    if (resolvedContextFor !== selectedFixtureId) return;
    const exists = fixtures.some((f) => f.id === selectedFixtureId);
    if (exists) return;

    if (fixtures.length > 0) {
      selectFixture(fixtures[0].id);
      return;
    }

    selectFixture("");
  }, [fixturesLoading, fixtures, selectedFixtureId, resolvingUrlFixture, resolvedContextFor, selectFixture]);

  // When a renderer is found deleted, this guard blocks the auto-select effect
  // below from immediately silently jumping to a different renderer — the whole
  // point of the deleted-renderer flow is to let the OPERATOR pick, via the
  // overlay opened in the effect further down. One-shot: any renderer picked
  // afterwards (auto or manual) clears it.
  const suppressAutoSelectRef = useRef(false);

  // Auto-select first unlocked renderer when fixture is selected but no renderer is chosen.
  useEffect(() => {
    if (suppressAutoSelectRef.current) return;
    if (selectedFixtureId && !selectedRendererId && renderers.length > 0) {
      const firstUnlocked = renderers.find((r) => !r.owner_lock?.locked);
      if (firstUnlocked) setSelectedRendererId(getRendererId(firstUnlocked));
    }
  }, [selectedFixtureId, selectedRendererId, renderers]);

  // Soft renderer-delete: the currently selected renderer no longer exists —
  // detected either by the live per-renderer poll (rendererDeleted prop) or by
  // it simply dropping out of the org's renderer list (the org-level fetch and
  // the poll are independent, so either can notice first). Either way: clear
  // the stale selection (state + localStorage) and open the renderer picker so
  // the operator chooses another one, instead of the old hard full-screen
  // block, or silently auto-picking a different renderer out from under them
  // (suppressAutoSelectRef prevents the auto-select effect above from doing
  // that immediately after this clear).
  useEffect(() => {
    if (!selectedRendererId) return;
    const missingFromList = !renderersLoading && renderers.length > 0
      && !renderers.some((r) => getRendererId(r) === selectedRendererId);
    if (!rendererDeleted && !missingFromList) return;

    suppressAutoSelectRef.current = true;
    setSelectedRendererId("");
    setRendererWasChanged(false);
    try {
      const stored = JSON.parse(localStorage.getItem("rendererSelection") || "{}");
      localStorage.setItem("rendererSelection", JSON.stringify({ ...stored, master: null }));
    } catch {
      localStorage.setItem("rendererSelection", JSON.stringify({ master: null, slaves: [] }));
    }
    onOpenRendererOverlay?.();
    // Refresh the org-level renderer list so the picker doesn't keep offering
    // the just-deleted renderer (or others deleted since this list was last
    // fetched) as a selectable option — picking one would just bounce the
    // operator right back into this same effect. Safe to call unconditionally:
    // once selectedRendererId is cleared above, the guard at the top of this
    // effect short-circuits before this branch runs again.
    fetchRenderers();
  }, [rendererDeleted, selectedRendererId, renderersLoading, renderers, onOpenRendererOverlay, fetchRenderers]);

  // Unique week numbers across the loaded fixture set, sorted numerically.
  // Used to populate the week dropdown.
  const availableWeeks = useMemo(() => {
    const set = new Set();
    fixtures.forEach((fx) => {
      if (fx.week != null && fx.week !== "") set.add(String(fx.week));
    });
    return Array.from(set).sort(
      (a, b) => (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0),
    );
  }, [fixtures]);

  // One-shot initialization: pick the current week as soon as fixtures load.
  // Skipped if the operator has already interacted with the dropdown (the
  // `hasInitializedWeek` ref-style guard prevents stomping their choice on
  // refetch).
  useEffect(() => {
    if (hasInitializedWeek) return;
    if (fixtures.length === 0) return;
    const week = getCurrentWeek(fixtures);
    setSelectedWeek(week || "all");
    setHasInitializedWeek(true);
  }, [fixtures, hasInitializedWeek]);

  const filteredFixtures = useMemo(() => {
    const query = search.trim().toLowerCase();
    const queryWords = query ? query.split(/\s+/) : [];
    const haystackFor = (fx) =>
      `${fx.team1} ${fx.team2} ${fx.location ?? ""}`.toLowerCase();
    const searchMatch = (fx) => queryWords.every((word) => haystackFor(fx).includes(word));

    // Upcoming mode: ignore week / date filters, keep only upcoming-or-live,
    // sort chronologically. Mirrors aether's Upcoming tab.
    if (viewMode === "upcoming") {
      const now = Date.now();
      return fixtures
        .filter((fx) => isUpcomingOrLive(fx.kickoffTs, fx.status, now) && searchMatch(fx))
        .slice()
        .sort((a, b) => (a.kickoffTs || 0) - (b.kickoffTs || 0));
    }

    return fixtures.filter((fx) => {
      const dateMatch = date ? fx.date === date.format("YYYY-MM-DD") : true;
      const weekMatch =
        !selectedWeek || selectedWeek === "all"
          ? true
          : String(fx.week ?? "") === selectedWeek;
      return dateMatch && weekMatch && searchMatch(fx);
    });
  }, [fixtures, date, selectedWeek, search, viewMode]);

  const currentWeek = useMemo(() => getCurrentWeek(fixtures), [fixtures]);
  const selectedWeekIndex = availableWeeks.indexOf(selectedWeek);
  const handleWeekPrev = () => {
    if (selectedWeekIndex > 0) setSelectedWeek(availableWeeks[selectedWeekIndex - 1]);
  };
  const handleWeekNext = () => {
    if (selectedWeekIndex >= 0 && selectedWeekIndex < availableWeeks.length - 1) {
      setSelectedWeek(availableWeeks[selectedWeekIndex + 1]);
    }
  };
  const weekPrevDisabled =
    !selectedWeek || selectedWeek === "all" || selectedWeekIndex <= 0;
  const weekNextDisabled =
    !selectedWeek ||
    selectedWeek === "all" ||
    selectedWeekIndex === -1 ||
    selectedWeekIndex >= availableWeeks.length - 1;

  useEffect(() => {
    if (selectedFixtureId) {
      const fx = fixtures.find((f) => f.id === selectedFixtureId);
      const rawFixture = fixtureData?.fixture?.find(
        (f) => f.fixture_id === selectedFixtureId
      );
      if (fx) {
        localStorage.setItem(
          "selectedFixture",
          JSON.stringify({
            ...fx,
            team1: fx.team1,
            team2: fx.team2,
            kickoff: fx.kickoff,
            date: fx.date,
            location: "TBD",
          })
        );
        localStorage.setItem("selectedFixtureId", selectedFixtureId);
        if (rawFixture?.competition_id)
          localStorage.setItem("selectedCompetitionId", rawFixture.competition_id);
        if (rawFixture?.season_id)
          localStorage.setItem("selectedSeasonId", rawFixture.season_id);
        sessionService.updateSession(selectedFixtureId, null).catch(console.error);
      }
    } else {
      localStorage.removeItem("selectedFixture");
      localStorage.removeItem("selectedFixtureId");
      localStorage.removeItem("selectedCompetitionId");
      localStorage.removeItem("selectedSeasonId");
      sessionService.updateSession(null, null).catch(console.error);
    }
  }, [selectedFixtureId, fixtures, fixtureData]);

  useEffect(() => {
    if (selectedRendererId) {
      const selection = JSON.parse(
        localStorage.getItem("rendererSelection") || "{}"
      );
      selection.master = selectedRendererId;
      localStorage.setItem("rendererSelection", JSON.stringify(selection));
      sessionService.updateSession(null, selectedRendererId).catch(console.error);
    } else {
      localStorage.setItem("rendererSelection", JSON.stringify({ master: null, slaves: [] }));
    }
  }, [selectedRendererId]);

  const bothSelected = Boolean(selectedFixtureId && selectedRendererId);
  // resolvingUrlFixture keeps TXLive from mounting against the WRONG fixture's
  // context on a deep link. TXLive reads selectedCompetitionId/selectedSeasonId
  // from localStorage at mount, and those are only written (below, from the
  // matched DDS row) once the deep-linked fixture is found in fixtureData — so
  // mounting early would hand it the previous session's competition/season.
  const isLoading = fixturesLoading || renderersLoading || resolvingUrlFixture;

  // ...but never blank the page out from under an OPEN picker. The operator is
  // mid-interaction there, and re-scoping the list (changing competition or
  // season) necessarily refetches — unmounting the overlay on every such
  // refetch would close the very dialog they are working in. The list below
  // renders its own loading state instead.
  const showFullPageLoader = isLoading && !fixtureOverlayOpen;

  const selectedFixture = fixtures.find((f) => f.id === selectedFixtureId);
  const selectedRenderer = renderers.find(
    (r) => r.id === selectedRendererId || r.renderer_id === selectedRendererId
  );
  const effectiveRendererId = selectedRenderer ? getRendererId(selectedRenderer) : "";

  // Resolve the renderer-lock verdict from all available sources, preferring the
  // live poll (rendererLockInfo, most current — also drives unlock recovery),
  // falling back to the ?session= snapshot on the stored session (available on
  // first paint of an alias/short-URL landing before the first poll resolves),
  // and finally to selectedRenderer.owner_lock (present the moment a renderer is
  // selected). Locked = view-only: TXLive still mounts, actions are disabled.
  //
  // sessionLockInfo is a ONE-TIME snapshot captured whenever the session/short
  // URL was originally resolved, for whichever renderer it pointed at THEN. It
  // must only apply while the operator is still looking at THAT SAME renderer —
  // otherwise, after switching to a different (unlocked) renderer via the
  // picker, this stale flag kept reporting locked=true for every renderer
  // picked afterwards, since nothing ever clears session.rendererLocked.
  const sessionLockInfo = session?.rendererLocked && session?.rendererId === effectiveRendererId
    ? {
        locked: true,
        lockedByName: session.rendererLockedByName || null,
        lockedAt: session.rendererLockedAt || null,
      }
    : null;
  const effectiveLockInfo = rendererLockInfo || sessionLockInfo;
  const { isRendererLocked, lockedByName, lockedAt } = deriveRendererLock({
    lockInfo: effectiveLockInfo,
    selectedRenderer,
    session, // needed so the raw owner_lock fallback honours the owner/super-admin exemption
  });

  useEffect(() => {
    if (onSelectionDisplayChange && bothSelected) {
      const fixtureLabel = selectedFixture ? `${selectedFixture.team1} v ${selectedFixture.team2}` : "";
      // Tri-code short label (e.g. "LIV v BOU") for the compact copilot footer.
      const fixtureShort = selectedFixture?.team1Tricode && selectedFixture?.team2Tricode
        ? `${selectedFixture.team1Tricode} v ${selectedFixture.team2Tricode}`
        : fixtureLabel;
      // Show ONLY the renderer's human name — the scene name is omitted because
      // appending it made the footer label too long. Never surface the
      // renderer_id here; operators identify renderers by name.
      const rendererName = selectedRenderer?.renderer_name || "";
      const sceneName = selectedRenderer?.scene?.name || "";
      const rendererLabel = rendererName || sceneName || "";
      const competitionId = localStorage.getItem("selectedCompetitionId");
      const seasonId = localStorage.getItem("selectedSeasonId");
      const aetherBaseUrl = process.env.REACT_APP_AETHER_URL;
    const aetherUrl = (competitionId && seasonId && selectedFixtureId)
        ? `${aetherBaseUrl}/teamsetup/${competitionId}/${seasonId}/${selectedFixtureId}`
        : null;
      const rendererId = selectedRenderer?.renderer_id || selectedRendererId;
      const opusBaseUrl = (process.env.REACT_APP_OPUS_URL || "").replace(/\/$/, "");
      const rendererDetailUrl = rendererId && opusBaseUrl
        ? `${opusBaseUrl}/dashboard/scene-controls/${encodeURIComponent(rendererId)}`
        : null;
      // Live preview URL — drives the footer's "open renderer preview" icon.
      const rendererPreviewBase = (process.env.REACT_APP_RENDERER_PREVIEW_URL || "").replace(/\/$/, "");
      const rendererPreviewUrl = rendererId && rendererPreviewBase
        ? `${rendererPreviewBase}/?renderer=${encodeURIComponent(rendererId)}`
        : null;
      onSelectionDisplayChange({ fixture: fixtureLabel, fixtureShort, renderer: rendererLabel, aetherUrl, rendererDetailUrl, rendererPreviewUrl, isRendererLocked, lockedByName });
    } else if (onSelectionDisplayChange) {
      onSelectionDisplayChange({ fixture: "", fixtureShort: "", renderer: "", aetherUrl: null, rendererDetailUrl: null, rendererPreviewUrl: null, isRendererLocked: false, lockedByName: null });
    }
  }, [bothSelected, selectedFixture, selectedRenderer, selectedRendererId, selectedFixtureId, onSelectionDisplayChange, isRendererLocked, lockedByName]);

  const fixtureOverlayContent = fixtureOverlayOpen ? (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", position: "relative" }}>
      {/* Fixed Header */}
      <Box sx={{ p: 4, pb: 2, flexShrink: 0 }}>
        {/* Title left, view-mode tabs in the middle slot pushed right,
            close (×) at the far right. Close used to be absolute-positioned
            and sat on top of the Upcoming toggle — moving it into the same
            flex row eliminates the overlap and keeps spacing predictable. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Select Fixture
          </Typography>
          <Box sx={{ flex: 1 }} />

          {/* Week / Upcoming view-mode tabs — separated from the filter row
              so the choice of "which slice of fixtures am I looking at" reads
              as a higher-level navigation step than the search/date filters
              below. Mirrors aether's Fixture page tabs. */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={(_, next) => next && setViewMode(next)}
            sx={{
              bgcolor: "#1c1c1c",
              borderRadius: "8px",
              ".MuiToggleButton-root": {
                color: "rgba(255,255,255,0.65)",
                border: "none",
                px: 2.5,
                py: 0.5,
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "6px",
              },
              ".MuiToggleButton-root.Mui-selected": {
                color: "#fff",
                bgcolor: "#2a2a2a",
              },
              ".MuiToggleButton-root.Mui-selected:hover": {
                bgcolor: "#333",
              },
            }}
          >
            <ToggleButton value="week">Week</ToggleButton>
            <ToggleButton value="upcoming">Upcoming</ToggleButton>
          </ToggleButtonGroup>

          <IconButton
            onClick={onCloseFixtureOverlay}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.8)",
              "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          <Typography sx={{ fontWeight: 600, minWidth: 80 }}>Filters:</Typography>

          <FormControl
            size="small"
            sx={{
              minWidth: 220,
              bgcolor: "#1c1c1c",
              ".MuiInputLabel-root": { color: "#bbb" },
              ".MuiOutlinedInput-root": { color: "#fff" },
              ".MuiSelect-icon": { color: "#fff" },
            }}
          >
            <InputLabel>Competition</InputLabel>
            <Select
              label="Competition"
              value={competitionId}
              onChange={(e) => handleCompetitionChange(e.target.value)}
              MenuProps={{ PaperProps: { sx: { bgcolor: "#121212", color: "#fff" } } }}
            >
              {/* Placeholder for the window before the allowed list lands, so
                  MUI has an option matching `value`. Once loading finishes the
                  reconcile effect guarantees competitionId IS in the list, so
                  the only lasting case here is an org with no competitions
                  assigned — which must read as empty, not as a live selection. */}
              {competitionId && !competitions.some((c) => c.competition_id === competitionId) && (
                <MenuItem value={competitionId}>
                  {competitionsLoading ? "Loading…" : "No competitions available"}
                </MenuItem>
              )}
              {competitions.map((c) => (
                <MenuItem key={c.competition_id} value={c.competition_id}>
                  {c.competition_name || c.competition_id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{
              minWidth: 200,
              bgcolor: "#1c1c1c",
              ".MuiInputLabel-root": { color: "#bbb" },
              ".MuiOutlinedInput-root": { color: "#fff" },
              ".MuiSelect-icon": { color: "#fff" },
            }}
          >
            <InputLabel>Season</InputLabel>
            <Select
              label="Season"
              value={seasonId}
              onChange={(e) => handleSeasonChange(e.target.value)}
              MenuProps={{ PaperProps: { sx: { bgcolor: "#121212", color: "#fff" } } }}
            >
              {seasonId && !competitionSeasons.some((s) => s.season_id === seasonId) && (
                <MenuItem value={seasonId}>Current season</MenuItem>
              )}
              {competitionSeasons.map((s) => (
                <MenuItem key={s.season_id} value={s.season_id}>
                  {s.season_name || s.season_id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search teams…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              bgcolor: "#1c1c1c",
              minWidth: 240,
              input: { color: "#fff" },
              "& .MuiInputBase-input::placeholder": { color: "#bbb", opacity: 1 },
            }}
          />
          {viewMode === "week" && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Select Date"
              value={date}
              onChange={setDate}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    bgcolor: "#1c1c1c",
                    input: { color: "#fff" },
                    label: { color: "#bbb" },
                    svg: { color: "#fff" },
                    minWidth: 220,
                  },
                },
                popper: {
                  sx: { ".MuiPaper-root": { bgcolor: "#121212", color: "#fff" } },
                },
                previousIconButton: { sx: { color: "#fff" } },
                nextIconButton: { sx: { color: "#fff" } },
              }}
            />
          </LocalizationProvider>
          )}

          {/* Week selector — mirrors Aether's WeekSelector (prev / dropdown
              / next). Defaults to the current week on first load; "All
              Weeks" disables the filter. Hidden when Upcoming is active. */}
          {viewMode === "week" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              onClick={handleWeekPrev}
              disabled={weekPrevDisabled}
              size="small"
              sx={{ border: "1px solid #444", borderRadius: 1, color: "#fff", "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" } }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <FormControl
              size="small"
              variant="outlined"
              sx={{
                minWidth: 140,
                bgcolor: "#1c1c1c",
                ".MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#888" },
                ".MuiInputLabel-root": { color: "#bbb" },
                ".MuiSelect-icon": { color: "#fff" },
                ".MuiInputBase-input": { color: "#fff" },
              }}
            >
              <InputLabel>Week</InputLabel>
              <Select
                value={selectedWeek || ""}
                label="Week"
                onChange={(e) => setSelectedWeek(e.target.value)}
              >
                <MenuItem value="all">
                  <em>All Weeks</em>
                </MenuItem>
                {availableWeeks.map((week) => {
                  const isCurrent = week === currentWeek;
                  return (
                    <MenuItem
                      key={week}
                      value={week}
                      sx={{
                        fontWeight: isCurrent ? "bold" : "normal",
                        color: isCurrent ? "#2fe6c8" : "inherit",
                      }}
                    >
                      Week {week}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <IconButton
              onClick={handleWeekNext}
              disabled={weekNextDisabled}
              size="small"
              sx={{ border: "1px solid #444", borderRadius: 1, color: "#fff", "&.Mui-disabled": { color: "rgba(255,255,255,0.3)" } }}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
          )}

          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setDate(null);
              setSelectedWeek("all");
              setSearch("");
              setViewMode("week");
            }}
            sx={{ borderColor: "#fff", color: "#fff" }}
          >
            Clear
          </Button>
        </Box>
      </Box>
      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflow: "auto", px: 4, pb: 4 }}>
        {fixturesLoading ? (
          // Re-scoping the list (new competition/season) empties it until the
          // new rows land. Say so, rather than showing a bare empty list that
          // reads as "this competition has no fixtures".
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6, gap: 1.5 }}>
            <CircularProgress size={20} sx={{ color: "#FCC144" }} />
            <Typography sx={{ color: "rgba(255,255,255,0.7)" }}>
              Loading fixtures…
            </Typography>
          </Box>
        ) : (
          <FixtureSelectionList
            fixtures={filteredFixtures}
            selectedFixtureId={selectedFixtureId}
            onSelect={(fx) => {
              selectFixture(fx.id);
              onCloseFixtureOverlay?.();
            }}
          />
        )}
      </Box>
    </Box>
  ) : null;

  const rendererOverlayContent = rendererOverlayOpen ? (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        p: 3,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        position: "relative",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <IconButton
        onClick={onCloseRendererOverlay}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          color: "rgba(255,255,255,0.8)",
          "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.1)" },
        }}
        size="small"
      >
        <CloseIcon />
      </IconButton>
      <Typography sx={{ fontWeight: 600, fontSize: "1rem", mb: 2, color: "#fff" }}>
        Change Renderer
      </Typography>

      {/* Scene filter — mirrors dashboard/RendererManagement: scenes built
          from each renderer's scene_id, "All scenes" is the empty state,
          filter compares by id (not name). Renderers without an allocated
          scene are NOT bucketed separately — they appear only under "All
          scenes", same as dashboard. */}
      {(() => {
        const scenesById = new Map();
        for (const r of renderers) {
          const id = r?.scene?.id;
          const name = r?.scene?.name;
          if (id && name && !scenesById.has(id)) {
            scenesById.set(id, name);
          }
        }
        if (scenesById.size === 0) return null;
        const scenes = Array.from(scenesById, ([id, name]) => ({ id, name }))
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        return (
          <FormControl
            size="small"
            variant="outlined"
            fullWidth
            sx={{
              mb: 1.5,
              bgcolor: "#121212",
              ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.3)" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.5)" },
              ".MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" },
              ".MuiSelect-icon": { color: "rgba(255,255,255,0.5)" },
              ".MuiInputBase-input": { color: "#fff" },
            }}
          >
            <InputLabel>Scene</InputLabel>
            <Select
              value={rendererSceneFilter}
              label="Scene"
              onChange={(e) => setRendererSceneFilter(e.target.value)}
            >
              <MenuItem value="">
                <em>All scenes</em>
              </MenuItem>
              {scenes.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      })()}

      <Autocomplete
        options={
          rendererSceneFilter
            ? renderers.filter((r) => r?.scene?.id === rendererSceneFilter)
            : renderers
        }
        // Display the renderer's human name (never the id). Falls back to
        // a clear placeholder if a renderer has no name set, so the input
        // never accidentally shows an OP-* identifier.
        getOptionLabel={(option) =>
          option?.renderer_name || "(unnamed renderer)"
        }
        // Selection state is still tracked by id internally so routing and
        // localStorage keys stay stable; only the display swaps to name.
        value={renderers.find((r) => getRendererId(r) === selectedRendererId) || null}
        isOptionEqualToValue={(option, value) =>
          getRendererId(option) === getRendererId(value)
        }
        onChange={(_, newValue) => {
          if (newValue) {
            suppressAutoSelectRef.current = false;
            setSelectedRendererId(getRendererId(newValue));
            setRendererWasChanged(true);
            onCloseRendererOverlay?.();
          }
        }}
        renderOption={(props, option) => {
          const isLocked = option.owner_lock?.locked === true;
          return (
            <Box component="li" {...props} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start !important", py: 1.5, opacity: isLocked ? 0.6 : 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                  {option.renderer_name || "(unnamed renderer)"}
                </Typography>
                {isLocked && (
                  <Typography sx={{ fontSize: "0.7rem", color: "warning.main", fontWeight: 700, letterSpacing: "0.04em" }}>
                    LOCKED
                  </Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.7)" }}>
                {isLocked
                  ? `Locked by ${option.owner_lock.locked_by_name || "a supervisor"}`
                  : `Scene: ${option.scene?.name || "Unallocated"}`}
              </Typography>
            </Box>
          );
        }}
        filterOptions={(options, { inputValue }) => {
          const searchTerm = inputValue.toLowerCase();
          if (!searchTerm) return options;
          return options.filter((option) => {
            const rendererName = (option.renderer_name || "").toLowerCase();
            const sceneName = (option.scene?.name || "").toLowerCase();
            return (
              rendererName.includes(searchTerm) ||
              sceneName.includes(searchTerm)
            );
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search renderer..."
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "#121212",
                color: "#fff",
                "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" },
                "&.Mui-focused fieldset": { borderColor: "#FEEC67" },
              },
              "& .MuiInputBase-input": { color: "#fff" },
              "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.5)", opacity: 1 },
            }}
          />
        )}
        slotProps={{
          paper: {
            sx: {
              bgcolor: "#121212",
              color: "#fff",
              maxHeight: 300,
              "& .MuiAutocomplete-option": {
                "&:hover": { bgcolor: "rgba(254, 236, 103, 0.1)" },
                "&[aria-selected='true']": { bgcolor: "rgba(254, 236, 103, 0.2)" },
              },
            },
          },
          clearIndicator: { sx: { color: "rgba(255,255,255,0.5)" } },
          popupIndicator: { sx: { color: "rgba(255,255,255,0.5)" } },
        }}
        fullWidth
      />
    </Box>
  ) : null;

  if (showFullPageLoader) {
    return (
      <Loader
        sequentialMessages={[
          "Loading fixtures...",
          "Loading renderers...",
          "Ready",
        ]}
      />
    );
  }

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", position: "relative" }}>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <TXLive
          // Remount the whole fixture subtree on a fixture SWITCH so every
          // useState/useRef re-initialises — clears stale dialogs (duplicate
          // detection, goal/clock/event-edit), refs and write-queues that
          // otherwise bleed across fixtures. Keyed on fixtureId only, so a
          // resync/tab-return (same fixtureId) does NOT remount.
          key={selectedFixtureId}
          orgId={orgId}
          fixtureId={selectedFixtureId}
          // Travels with the competition rather than through localStorage, so
          // two tabs on two competitions cannot overwrite each other's profile.
          selectedProfileId={selectedProfileId}
          rendererId={effectiveRendererId}
          fixtureOverlayOpen={fixtureOverlayOpen}
          fixtureOverlayContent={fixtureOverlayContent}
          onCloseFixtureOverlay={onCloseFixtureOverlay}
          rendererOverlayOpen={rendererOverlayOpen}
          rendererOverlayContent={rendererOverlayContent}
          isLive={isLive}
          isNleEnabled={isNleEnabled}
          onCloseRendererOverlay={onCloseRendererOverlay}
          onOpenRendererOverlay={onOpenRendererOverlay}
          showChangeRendererButton={rendererWasChanged}
          hasFixtureSelected={!!selectedFixtureId}
          hasRendererSelected={!!selectedRenderer}
          readOnly={isRendererLocked}
          lockInfo={{ locked: isRendererLocked, lockedByName, lockedAt }}
          onLineupErrorsChange={onLineupErrorsChange}
          onBrokerStatusChange={onBrokerStatusChange}
          ddsViewerOpen={ddsViewerOpen}
          onCloseDdsViewer={onCloseDdsViewer}
        />
      </Box>
    </Box>
  );
}
